import { lumi } from '@/lib/lumi'
import type { HostAccount } from '@/types/host'
import { MessageCircleHeart, X } from 'lucide-react'
import { FormEvent, useEffect, useState } from 'react'

const EMPTY = { hostId: '', senderName: '', phone: '', city: '', message: '' }

function MessageBox() {
  const [open, setOpen] = useState(false)
  const [hosts, setHosts] = useState<HostAccount[]>([])
  const [loadingHosts, setLoadingHosts] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    setLoadingHosts(true)
    lumi.entities.hosts
      .list({ filter: { active: true }, sort: { fullName: 1 }, limit: 100, projection: { fullName: 1, photoUrl: 1, active: 1 } })
      .then(({ list }) => {
        if (!cancelled) setHosts((list || []) as HostAccount[])
      })
      .catch((err) => {
        console.error('Falha ao carregar locutores:', err)
        if (!cancelled) setError('Não foi possível carregar os locutores. Tente novamente.')
      })
      .finally(() => {
        if (!cancelled) setLoadingHosts(false)
      })
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      cancelled = true
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const close = () => {
    setOpen(false)
    setSent(false)
    setError('')
    setForm(EMPTY)
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    const host = hosts.find((item) => item._id === form.hostId)
    if (!host || !host._id) {
      setError('Escolha o locutor que vai receber o recadinho.')
      return
    }
    setSending(true)
    try {
      await lumi.entities.host_messages.create({
        hostId: host._id,
        hostName: host.fullName,
        senderName: form.senderName.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        message: form.message.trim(),
        read: false,
        status: 'pending',
        createdAt: new Date().toISOString(),
      })
      setSent(true)
      setForm(EMPTY)
    } catch (err) {
      console.error('Falha ao enviar recadinho:', err)
      setError('Não foi possível enviar agora. Tente novamente em instantes.')
    } finally {
      setSending(false)
    }
  }

  const inputClass = 'mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900'

  return (
    <>
      <div className="mx-auto mt-6 flex max-w-3xl flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-full border border-white/15 bg-gradient-to-r from-[#ffd21f] via-[#ffdc4a] to-[#f5b400] px-3 py-2 text-[#12307a] sm:mt-8 sm:px-4">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#0b2ea8] text-[#ffd21f] sm:h-9 sm:w-9">
          <MessageCircleHeart size={18} />
        </span>
        <p className="min-w-0 text-center text-xs font-bold leading-tight sm:text-sm">
          Mande um recadinho para o seu locutor favorito!
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="recado-cta group relative shrink-0 overflow-hidden rounded-full px-4 py-2 text-[11px] font-extrabold uppercase tracking-wider text-white sm:text-xs"
        >
          <span className="recado-cta-shine" aria-hidden="true" />
          <span className="relative flex items-center justify-center gap-1.5">
            <MessageCircleHeart size={15} className="recado-cta-icon" />
            Enviar recadinho
          </span>
        </button>
      </div>

      {open ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="recado-title">
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-[28px] bg-white p-5 text-slate-900 shadow-2xl sm:rounded-[28px] sm:p-7">
            <div className="flex items-start justify-between gap-3">
              <h3 id="recado-title" className="font-display text-2xl font-black text-[#0b2ea8]">Enviar recadinho</h3>
              <button type="button" onClick={close} aria-label="Fechar" className="rounded-full p-2 text-slate-500 hover:bg-slate-100">
                <X size={20} />
              </button>
            </div>

            {sent ? (
              <div className="py-8 text-center">
                <p className="text-4xl" aria-hidden="true">💙</p>
                <p className="mt-3 text-lg font-bold">Recadinho enviado!</p>
                <p className="mt-1 text-sm text-slate-600">A rádio vai analisar e, depois de aprovada, o locutor recebe a sua mensagem.</p>
                <button type="button" onClick={close} className="mt-6 rounded-full bg-[#0b2ea8] px-8 py-3 font-bold text-white">
                  Fechar
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-4 grid gap-3">
                <label className="block text-sm font-semibold">
                  Para qual locutor?
                  <select
                    value={form.hostId}
                    onChange={(e) => setForm({ ...form, hostId: e.target.value })}
                    className={inputClass}
                    required
                    disabled={loadingHosts}
                  >
                    <option value="">{loadingHosts ? 'A carregar...' : 'Selecione o locutor'}</option>
                    {hosts.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.fullName}
                      </option>
                    ))}
                  </select>
                </label>
                {!loadingHosts && !hosts.length ? (
                  <p className="text-xs text-slate-500">Ainda não há locutores disponíveis para receber recadinhos.</p>
                ) : null}
                <label className="block text-sm font-semibold">
                  Seu nome
                  <input value={form.senderName} onChange={(e) => setForm({ ...form, senderName: e.target.value })} className={inputClass} required maxLength={80} autoComplete="name" />
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm font-semibold">
                    Telefone
                    <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} required maxLength={30} autoComplete="tel" placeholder="(84) 90000-0000" />
                  </label>
                  <label className="block text-sm font-semibold">
                    Cidade
                    <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputClass} required maxLength={80} autoComplete="address-level2" />
                  </label>
                </div>
                <label className="block text-sm font-semibold">
                  Sua mensagem
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputClass} min-h-[110px]`} required maxLength={500} />
                </label>
                {error ? <p className="text-sm font-semibold text-red-600">{error}</p> : null}
                <button type="submit" disabled={sending || loadingHosts} className="rounded-full bg-[#0b2ea8] py-3 font-extrabold text-white disabled:opacity-60">
                  {sending ? 'Enviando...' : 'Enviar recadinho'}
                </button>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </>
  )
}

export default MessageBox
